import React, { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Icon, Button } from '../../../pages/DesignSystem';
import './PublishChecklistModal.css';

interface PublishChecklistModalProps {
  isOpen: boolean;
  onClose: () => void;
  missingFields: string[];
  onEditClick: () => void;
}

/**
 * PublishChecklistModal - Shows a list of required fields that are missing
 * before an event can be published.
 */
const PublishChecklistModal: React.FC<PublishChecklistModalProps> = ({
  isOpen,
  onClose,
  missingFields,
  onEditClick
}) => {
  // Handle ESC key to close
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [isOpen, onClose]);

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="publish-checklist-modal__overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          onClick={handleOverlayClick}
        >
          <motion.div
            className="publish-checklist-modal__dialog"
            initial={{ opacity: 0, scale: 0.95, y: -20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: -20 }}
            transition={{ duration: 0.2 }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="publish-checklist-modal__header">
              <Icon name="AlertCircle" size={24} className="publish-checklist-modal__icon" />
              <h2 className="publish-checklist-modal__title">Cannot Publish Yet</h2>
            </div>

            <p className="publish-checklist-modal__intro">
              Please complete the following required fields before publishing:
            </p>

            <ul className="publish-checklist-modal__list">
              {missingFields.map(field => (
                <li key={field} className="publish-checklist-modal__item">
                  <Icon name="XCircle" size={16} className="publish-checklist-modal__item-icon" />
                  <span>{field}</span>
                </li>
              ))}
            </ul>

            <div className="publish-checklist-modal__actions">
              <Button variant="secondary" onClick={onClose}>
                Cancel
              </Button>
              <Button variant="primary" onClick={onEditClick}>
                Edit Event
              </Button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default PublishChecklistModal;
